import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  deleteSeedVarietyProduct,
  deleteSeedVarietyVariant,
  fetchSeedVarietiesAdmin,
  getToken,
  updateSeedVarietyProduct,
} from './api';
import { input, label } from './adminUi';

export default function SeedVarietyProductEdit() {
  const { productId: productIdParam } = useParams();
  const navigate = useNavigate();
  const decodedId = productIdParam ? decodeURIComponent(productIdParam) : '';
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState('');
  const [productLineLabel, setProductLineLabel] = useState('');
  const [summary, setSummary] = useState('');
  const [variants, setVariants] = useState([]);
  const [loadState, setLoadState] = useState('loading');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!getToken()) {
      navigate('/admin', { replace: true });
      return;
    }
    let cancelled = false;
    fetchSeedVarietiesAdmin()
      .then((block) => {
        if (cancelled) return;
        const product = block?.families?.find((f) => f.id === decodedId);
        if (!product) {
          setLoadState('notfound');
          return;
        }
        setCategories(Array.isArray(block?.categories) ? block.categories : []);
        setCategoryId(product.categoryId || '');
        setProductLineLabel(product.familyLabel || '');
        setSummary(product.summary || '');
        setVariants(Array.isArray(product.variants) ? product.variants : []);
        setLoadState('ready');
      })
      .catch(() => {
        if (!cancelled) setLoadState('notfound');
      });
    return () => {
      cancelled = true;
    };
  }, [navigate, decodedId]);

  async function saveMeta(e) {
    e.preventDefault();
    setErr('');
    setMsg('');
    setSaving(true);
    try {
      await updateSeedVarietyProduct(decodedId, { categoryId, productLabel: productLineLabel, summary });
      setMsg('Saved.');
    } catch (e) {
      setErr(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function removeVariant(v) {
    if (!window.confirm(`Remove variant “${v.name || v.id}”?`)) return;
    setErr('');
    setMsg('');
    setBusy(true);
    try {
      const next = await deleteSeedVarietyVariant(decodedId, v.id);
      const product = next?.families?.find((f) => f.id === decodedId);
      setVariants(Array.isArray(product?.variants) ? product.variants : []);
      setMsg('Variant removed.');
    } catch (e) {
      setErr(e.message || 'Remove failed');
    } finally {
      setBusy(false);
    }
  }

  async function removeProduct() {
    if (!window.confirm('Delete this entire product and all variants?')) return;
    setBusy(true);
    try {
      await deleteSeedVarietyProduct(decodedId);
      navigate('/admin/dashboard/seed-varieties');
    } catch (e) {
      setErr(e.message || 'Delete failed');
      setBusy(false);
    }
  }

  if (loadState === 'loading') {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-neutral-600">Loading…</div>
    );
  }

  if (loadState === 'notfound') {
    return (
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-8 md:px-8">
        <p className="text-neutral-700">Product not found.</p>
        <Link to="/admin/dashboard/seed-varieties" className="mt-4 inline-block text-sm font-semibold text-brand-lime">
          ← Back
        </Link>
      </div>
    );
  }

  return (
    <>
      <header className="z-10 flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-neutral-200 bg-white px-4 py-3 md:px-8">
        <div>
          <Link
            to="/admin/dashboard/seed-varieties"
            className="text-xs font-medium text-brand-olive hover:underline"
          >
            ← Variety specs
          </Link>
          <h2 className="mt-1 text-lg font-bold text-neutral-900 md:text-xl">Edit product</h2>
          <p className="mt-0.5 font-mono text-xs text-neutral-500">id: {decodedId}</p>
        </div>
        <button
          type="button"
          disabled={busy}
          onClick={removeProduct}
          className="rounded-lg border border-red-200 bg-white px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
        >
          Delete product
        </button>
      </header>

      <main className="min-h-0 w-full min-w-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-y-contain px-4 py-6 md:px-8 md:py-8">
        {msg ? <p className="mb-4 text-sm text-green-700">{msg}</p> : null}
        {err ? <p className="mb-4 text-sm text-red-600">{err}</p> : null}

        <form
          onSubmit={saveMeta}
          className="mb-10 w-full max-w-none rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm"
        >
          <h3 className="text-sm font-semibold text-neutral-800">Details</h3>
          <label className={`${label} mt-4`}>
            Category
            <select className={input} value={categoryId} onChange={(e) => setCategoryId(e.target.value)} required>
              <option value="">Select category…</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name || c.id}
                </option>
              ))}
            </select>
          </label>
          <label className={`${label} mt-4`}>
            Product line label (optional)
            <input className={input} value={productLineLabel} onChange={(e) => setProductLineLabel(e.target.value)} />
          </label>
          <label className={`${label} mt-4`}>
            Summary
            <textarea className={`${input} min-h-[5rem]`} value={summary} onChange={(e) => setSummary(e.target.value)} />
          </label>
          <button
            type="submit"
            disabled={saving}
            className="mt-6 rounded-lg bg-brand-forest px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save details'}
          </button>
        </form>

        <section className="w-full max-w-none rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-neutral-800">Variants</h3>
            <Link
              to={`/admin/dashboard/seed-varieties/products/${encodeURIComponent(decodedId)}/variants/new`}
              className="rounded-lg bg-brand-forest px-4 py-2 text-xs font-semibold text-white hover:opacity-90"
            >
              + New variant
            </Link>
          </div>
          {variants.length === 0 ? (
            <p className="mt-4 text-sm text-neutral-500">No variants yet. Add one to show on the public sheet.</p>
          ) : (
            <ul className="mt-4 divide-y divide-neutral-100 border border-neutral-100 rounded-xl">
              {variants.map((v) => (
                <li key={v.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                  <div>
                    <p className="font-medium text-neutral-900">{v.name || 'Untitled'}</p>
                    <p className="font-mono text-xs text-neutral-500">{v.id}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Link
                      to={`/admin/dashboard/seed-varieties/products/${encodeURIComponent(decodedId)}/variants/${encodeURIComponent(v.id)}/edit`}
                      className="rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-800 hover:bg-neutral-50"
                    >
                      Edit
                    </Link>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => removeVariant(v)}
                      className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </>
  );
}
